import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type BannerVariant = 'info' | 'warning' | 'urgent';

export interface TrialBannerState {
  // Dismissed banner IDs with timestamps (for 24h snooze)
  dismissedBanners: Record<string, number>; // bannerId -> dismissedAt timestamp

  // Actions
  dismissBanner: (bannerId: string) => void;
  isBannerDismissed: (bannerId: string) => boolean;
  clearExpiredDismissals: () => void;
}

// 24 hours in milliseconds
const SNOOZE_DURATION = 24 * 60 * 60 * 1000;

export const useNotificationStore = create<TrialBannerState>()(
  persist(
    (set, get) => ({
      dismissedBanners: {},

      dismissBanner: (bannerId: string) => {
        set((state) => ({
          dismissedBanners: {
            ...state.dismissedBanners,
            [bannerId]: Date.now(),
          },
        }));
      },

      isBannerDismissed: (bannerId: string) => {
        const { dismissedBanners } = get();
        const dismissedAt = dismissedBanners[bannerId];

        if (!dismissedAt) {
          return false;
        }

        // Check if 24 hours have passed
        const now = Date.now();
        const isExpired = now - dismissedAt > SNOOZE_DURATION;

        if (isExpired) {
          // Clean up expired dismissal
          const { [bannerId]: _, ...rest } = dismissedBanners;
          set({ dismissedBanners: rest });
          return false;
        }

        return true;
      },

      clearExpiredDismissals: () => {
        const { dismissedBanners } = get();
        const now = Date.now();

        const validDismissals: Record<string, number> = {};

        for (const [bannerId, dismissedAt] of Object.entries(dismissedBanners)) {
          if (now - dismissedAt <= SNOOZE_DURATION) {
            validDismissals[bannerId] = dismissedAt;
          }
        }

        set({ dismissedBanners: validDismissals });
      },
    }),
    {
      name: 'zupa-notifications',
      partialize: (state) => ({
        dismissedBanners: state.dismissedBanners,
      }),
    }
  )
);
